import { useEffect, useRef, useState } from "react";

/**
 * Hook that triggers a reveal animation when the element scrolls into view.
 * Returns [ref, isVisible] — attach ref to the element, use isVisible to toggle styles.
 */
export default function useScrollReveal(options = {}) {
  const { threshold = 0.15, once = true } = options;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once]);

  return [ref, isVisible];
}

/**
 * Wrapper component that applies scroll-triggered fade+slide animation.
 * @param {object} props
 * @param {string} props.direction - "up" | "left" | "right" (default "up")
 * @param {number} props.delay - delay in ms (default 0)
 * @param {number} props.duration - duration in ms (default 600)
 * @param {object} props.style - additional inline styles
 */
export function ScrollReveal({ children, direction = "up", delay = 0, duration = 600, style = {}, ...props }) {
  const [ref, isVisible] = useScrollReveal();

  const transforms = {
    up: "translateY(40px)",
    left: "translateX(-40px)",
    right: "translateX(40px)",
    down: "translateY(-40px)",
  };

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translate(0, 0)" : transforms[direction] || transforms.up,
        transition: `opacity ${duration}ms ease ${delay}ms, transform ${duration}ms ease ${delay}ms`,
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Animated counter that counts up from 0 to target when scrolled into view.
 */
export function AnimatedCounter({ value, duration = 2000, prefix = "", suffix = "", style = {} }) {
  const [ref, isVisible] = useScrollReveal();
  const [count, setCount] = useState(0);
  const numericValue = typeof value === "string" ? parseFloat(value.replace(/[^0-9.]/g, "")) : value;
  const isDecimal = String(numericValue).includes(".");

  useEffect(() => {
    if (!isVisible || !numericValue) return;

    let start = 0;
    const startTime = Date.now();

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * numericValue;
      setCount(current);
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [isVisible, numericValue, duration]);

  const display = isDecimal ? count.toFixed(1) : Math.round(count).toLocaleString();

  return (
    <span ref={ref} style={style}>
      {prefix}{display}{suffix}
    </span>
  );
}
